import _, { find, get, startCase } from "lodash";
import { Badge } from "@shared/ui";
import React, { useEffect, useMemo, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import {
  CARRIER_STATUS,
  CARRIER_STATUS_SUPPORTED_FILTER,
} from "../lib/constants";
import { getCarriers } from "../lib/automatic";
import seedData from "../seed/carriers.json";
import LoadingSpinner from "./LoadingSpinner";
import Carrier from "./table/Carrier";
import {
  SelectColumnFilter,
  StatusSelectColumnFilter,
} from "./table/Filter";
import Table from "./table/Table";

const isSupported = (status) =>
  Boolean(status) && status !== CARRIER_STATUS.UNSUPPORTED;

// Status filter dropdown options. Includes a synthetic "Supported" option that
// mirrors the sentinel value used by the quick filters so the column dropdown
// stays in sync when a user clicks "User portal" or "Policy lookup".
const USER_PORTAL_STATUS_OPTIONS = [
  { value: CARRIER_STATUS_SUPPORTED_FILTER, label: "Supported" },
  { value: CARRIER_STATUS.ACTIVE, label: "Active" },
  { value: CARRIER_STATUS.ACTIVE_BASIC, label: "Active - basic" },
  { value: CARRIER_STATUS.INACTIVE, label: "Inactive" },
  { value: CARRIER_STATUS.UNSUPPORTED, label: "Unsupported" },
];

// Policy lookup retains the simpler active/inactive/unsupported model — no "active-basic".
const POLICY_LOOKUP_STATUS_OPTIONS = [
  { value: CARRIER_STATUS_SUPPORTED_FILTER, label: "Supported" },
  { value: CARRIER_STATUS.ACTIVE, label: "Active" },
  { value: CARRIER_STATUS.INACTIVE, label: "Inactive" },
  { value: CARRIER_STATUS.UNSUPPORTED, label: "Unsupported" },
];

// User-portal labels are a superset of the policy-lookup statuses.
const statusLabel = (value) =>
  find(USER_PORTAL_STATUS_OPTIONS, { value })?.label;

const toCsvRow = (carrier) => ({
  Carrier: carrier.name,
  Domain: carrier.domain,
  "Supported policies": carrier.policyTypes?.map(startCase).join(", "),
  "User portal": statusLabel(carrier.userPortalStatus),
  "Policy lookup": statusLabel(carrier.policyLookupStatus),
});

// Normalize a carrier record so the UI can render the caveated "active-basic"
// state even when the upstream API has not yet been redeployed with the new
// status model. Safe to remove once `automatic` is fully rolled out.
const normalizeCarrier = (carrier) => {
  if (
    carrier?.userPortalStatus === CARRIER_STATUS.ACTIVE &&
    carrier?.type === "basic"
  ) {
    return { ...carrier, userPortalStatus: CARRIER_STATUS.ACTIVE_BASIC };
  }
  return carrier;
};

const normalizeCarriers = (carriers) =>
  Array.isArray(carriers) ? carriers.map(normalizeCarrier) : [];

// Custom react-table filter that supports two values:
// - CARRIER_STATUS_SUPPORTED_FILTER matches any row whose value is not "unsupported"
// - any other value performs exact match (parity with the built-in "exact" filter)
const supportedOrExactFilter = (rows, columnIds, filterValue) => {
  if (filterValue === undefined || filterValue === null) return rows;
  const [id] = columnIds;
  if (filterValue === CARRIER_STATUS_SUPPORTED_FILTER) {
    return rows.filter((row) => isSupported(row.values[id]));
  }
  return rows.filter((row) => row.values[id] === filterValue);
};

const calculateMarketShare = (carriers, predicate, policyType) => {
  const filteredCarriers = predicate
    ? carriers.filter(predicate)
    : carriers;

  const totalMarketShare = filteredCarriers.reduce((sum, carrier) => {
    return get(carrier, `share.${policyType}`)
      ? sum + get(carrier, `share.${policyType}`)
      : sum;
  }, 0);

  return totalMarketShare.toLocaleString(undefined, {
    style: "percent",
    maximumFractionDigits: 0,
  });
};

const formatSupportedSummary = (supported) => `${supported} supported`;

// Coverage market-share line, rendered on a separate sub-line under the
const formatCoverageSubLabel = (carriers) =>
  `${calculateMarketShare(carriers, null, "auto")} auto + ${calculateMarketShare(
    carriers,
    null,
    "home"
  )} home coverage`;

const CarriersTable = () => {
  const { promiseInProgress } = usePromiseTracker();
  const [carriers, setCarriers] = useState([]);

  const columns = useMemo(
    () => [
      {
        Header: "Carrier",
        accessor: (row) => {
          const { id, name, domain, hasImage } = row;
          return JSON.stringify({ id, name, domain, hasImage });
        },
        Cell: ({ value }) => {
          const carrier = JSON.parse(value);
          return <Carrier carrier={carrier} />;
        },
        width: 400,
      },

      {
        Header: "Supported policies",
        Filter: SelectColumnFilter,
        accessor: "policyTypes",
        Cell: ({ value }) => {
          const startCaseVals = value?.map((val) => startCase(val));
          return _(startCaseVals).join(", ");
        },
        width: 100,
      },

      {
        Header: "User portal",
        Filter: StatusSelectColumnFilter({
          options: USER_PORTAL_STATUS_OPTIONS,
        }),
        accessor: "userPortalStatus",
        filter: supportedOrExactFilter,
        Cell: ({ value }) => {
          return <Badge status={value} />;
        },
        width: 140,
      },

      {
        Header: "Policy lookup",
        Filter: StatusSelectColumnFilter({
          options: POLICY_LOOKUP_STATUS_OPTIONS,
        }),
        accessor: "policyLookupStatus",
        filter: supportedOrExactFilter,
        Cell: ({ value }) => {
          return <Badge status={value} />;
        },
        width: 140,
      },
    ],
    []
  );

  // All integrations: supported for either product area (user portal OR policy lookup).
  const allIntegrationsSupportedCount = carriers.filter(
    (c) => isSupported(c.userPortalStatus) || isSupported(c.policyLookupStatus)
  ).length;

  const userPortalSupportedCount = carriers.filter((c) =>
    isSupported(c.userPortalStatus)
  ).length;

  const policyLookupSupportedCount = carriers.filter((c) =>
    isSupported(c.policyLookupStatus)
  ).length;

  const quickFilters = [
    {
      id: 1,
      label: "All integrations",
      // First line: support count only — keeps the header focused on coverage breadth.
      subLabel: formatSupportedSummary(allIntegrationsSupportedCount),
      // Second line: market-share coverage detail, lifted out of the support
      // line so it reads cleanly on desktop instead of being separated by a dot.
      secondarySubLabel: formatCoverageSubLabel(carriers),
      // All Integrations clears the status filter so unsupported carriers remain visible.
      // QuickFilter already clears filters on all other quick-filter columns.
      column: "userPortalStatus",
      value: null,
    },
    {
      id: 2,
      label: "User portal",
      subLabel: formatSupportedSummary(userPortalSupportedCount),
      column: "userPortalStatus",
      value: CARRIER_STATUS_SUPPORTED_FILTER,
    },
    {
      id: 3,
      label: "Policy lookup",
      subLabel: formatSupportedSummary(policyLookupSupportedCount),
      column: "policyLookupStatus",
      value: CARRIER_STATUS_SUPPORTED_FILTER,
    },
  ];

  useEffect(() => {
    if (process.env.REACT_APP_MOCK === "true") {
      setCarriers(normalizeCarriers(seedData));
      return;
    }

    trackPromise(
      getCarriers().then((data) => {
        setCarriers(normalizeCarriers(data));
      })
    );
  }, []);

  return (
    <>
      {promiseInProgress === true ? (
        <LoadingSpinner />
      ) : (
        <>
          <Table
            entity="carriers"
            data={carriers}
            columns={columns}
            resourceKey="id"
            globalFilterPlaceholder={
              "Enter any carrier name or portal status..."
            }
            quickFilters={quickFilters}
            csvRow={toCsvRow}
          />
        </>
      )}
    </>
  );
};

export default CarriersTable;
